import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Drawer, Skeleton, Stack, useMediaQuery } from "@material-ui/core";
import { useSnackbar } from "notistack5";
import useCrmMutations from "hooks/crm/useCrmMutations";
import useCrmMyAccess from "hooks/crm/useCrmMyAccess";
import useContactBook from "hooks/contacts/useContactBook";
import useContactWrites from "hooks/contacts/useContactWrites";
import { isOrgBookKey } from "hooks/contacts/contactBookRules";
import { normalizeBookKey } from "hooks/contacts/contactDocumentStore";
import useAuth from "hooks/useAuth";
import useWebotServer from "hooks/useWebotServer";
import { useTheme } from "@material-ui/core/styles";
import { PATH_DASHBOARD } from "routes/paths";
import { describeContactWriteFailure } from "utils/contacts/contactWriteErrors";
import CrmContactPanel from "../../crm/panel/CrmContactPanel";
import { crmPooledRowAccess } from "../../crm/crmRowAccess";
import type { CrmPooledRowActions } from "../../crm/crmRowAccess";
import type { CrmContactRow } from "../../../../@types/webotServer";

/**
 * A contact's side panel, openable from ANYWHERE — including over an open dialog.
 *
 * ## It renders the CRM's panel, not the contact PAGE
 *
 * The first version mounted `ContactDetailView`, and that was the whole of the
 * reported layout mess. `ContactDetailView` is the full-PAGE view: it brings a
 * "חזרה ללקוחות" back bar, a page-width header and a tab strip sized for a whole
 * screen, and several of its files ask the VIEWPORT how much room they have — so
 * on a 1920 monitor it laid itself out for 1920 and was then painted into a 560px
 * drawer.
 *
 * `CrmContactPanel` is the component actually built for this shape, and the CRM
 * page mounts it in its own docked column. It depends on nothing from that page —
 * only global hooks — it renders its own header, identity and close button, and it
 * measures itself through `useContactSurface` internally, so this file must NOT
 * wrap it in a surface provider or add a header of its own.
 *
 * ## Same shapes as /crm
 *
 * From `md` up it is a right-hand column in the `docked` presentation; below `md` it is the
 * CRM's 92vh bottom sheet in the `sheet` presentation — the breakpoint and shapes
 * `CrmWorkspace` uses (`canDockPanel`), so the panel looks and behaves the same wherever it
 * is opened.
 *
 * `row` / `scopeIsOwnBook` / `orgId` / `canEdit` are passed straight through with /crm's
 * meaning. A colleague's row (its `ownerUserId` is not the viewer) opens own-first exactly
 * like the "של המשרד" tab: the viewer's own copy of the person if there is one, otherwise
 * the viewer's timeline with the client.
 *
 * ## Without a `row` (a listing's seller): the drawer asks for it
 *
 * The panel draws the status and 🔥 strip from the ROW, never from the document (see
 * `CrmContactPanel`), and a shared office's "הלקוחות שלי" line from the row's holder list. A
 * caller with no list row (the listing dialog's seller) used to get neither: a seller's panel
 * had no status chip at all, and in a shared office no way to add the seller to "הלקוחות שלי"
 * or take them off it. So when `row` is not passed the drawer asks `/contacts/lookup` for the
 * contact's row — the same `CrmContactRow` /crm lists, over the viewer's whole book (a shared
 * office's included, with its holders) — and mounts the panel once that answer is in (a
 * failure, or a phone that is no customer, mounts it with no row, as before). The wait is one
 * request, and it is what lets a shared office's panel read under the office's scope from its
 * first request: a colleague's customer read under `mine` is "not found".
 *
 * ## A shared office's record: the "הלקוחות שלי" line
 *
 * A row from the viewer's own shared book (its `org:` key) gets the line /crm's rows get (the
 * same rule, `crmPooledRowAccess`), with add and remove run here; the office's DELETE is not
 * offered, it lives behind /crm's ⋮ only. The panel then reads under the office's scope, as
 * /crm's office tab does: a held record is in the office book too.
 *
 * ## Stacking — the MODAL layer, never above it
 *
 * ⚠ A `Drawer` sits at `theme.zIndex.drawer` (1200), BELOW `theme.zIndex.modal` (1300):
 * opened from a dialog at the default it renders BEHIND that dialog and reads as nothing
 * happening. So it is raised to the modal layer — and EXACTLY to it, not past it. Every
 * dialog, menu, popover and select is a modal at 1300, and between equals the later portal
 * wins: this drawer, opened over the listing dialog, lands above it, and everything the
 * panel opens (its edit dialog, the status and handler pickers, a tab's menus) lands above
 * the drawer. It used to be `modal + 2`, which put every one of those BEHIND the panel that
 * opened them.
 *
 * MUI's modal manager handles the nesting — the most recently opened modal takes the focus
 * trap — so the dialog underneath keeps its state and is still there when the panel closes.
 *
 * ## Height
 *
 * `CrmContactPanel`'s root is `height: 100%, minHeight: 0`: it owns its own
 * scrollport, with the header fixed and only the tab panels scrolling. That
 * resolves only against a parent with a DEFINITE height — which is why the paper
 * has one, with `overflow: hidden`, matching the `height` + `overflow` contract the
 * CRM's own Card sets for it. Without it the column falls back to content height and
 * the panel simply clips.
 */
export function ContactPanelDrawer({
  contactId,
  onClose,
  row,
  scopeIsOwnBook,
  orgId,
  canEdit,
  onContactPatched,
  onContactRekeyed
}: {
  /** The contact to show. `null` closes the panel. */
  contactId: string | null;
  onClose: () => void;
  /**
   * The list row it was opened from (/crm's `CrmContactRow`). NOT PASSED = the drawer asks the
   * server for the row itself (see the header); `null` = there is none, and none is asked for.
   */
  row?: CrmContactRow | null;
  /** False when the row may be a colleague's — see `CrmContactPanel`. Default true. */
  scopeIsOwnBook?: boolean;
  /** The agency the row came from — what the מתווך מטפל control may set. */
  orgId?: string | null;
  canEdit?: boolean;
  onContactPatched?: (patch: Partial<CrmContactRow>) => void;
  onContactRekeyed?: (oldContactId: string, savedRow: Partial<CrmContactRow> & { contactId: string }) => void;
}): JSX.Element {
  const theme = useTheme();
  // The CRM's own breakpoint for "is there room to dock the panel" (`canDockPanel`).
  const canDock = useMediaQuery(theme.breakpoints.up("md"));
  const { enqueueSnackbar } = useSnackbar();

  // No row from the caller: the server's row for this contact (see the header).
  const ownRow = row === undefined;
  const asked = useContactRowOf(ownRow ? contactId : null);
  const baseRow = ownRow ? asked.row : row;

  /**
   * Status and the hot flag are editable HERE, not only on /crm.
   *
   * The panel has always been able to DRAW the controls — it just had nobody to call, so this
   * drawer showed a read-only card. `useCrmMutations` is the same writer /crm uses, and it exists
   * precisely because `useLegacyContactWrites().updateContact` would look the contact up in the 25,000-row
   * in-browser array this page never loads, take its create branch, and corrupt the derived
   * `customerSeriousness` mirror on every inline edit.
   *
   * The optimistic patch is kept LOCALLY as well as forwarded: `row` is a prop owned by the caller,
   * so without an overlay the picker would close back onto the old status until the caller
   * happened to re-render.
   */
  const [patch, setPatch] = useState<Partial<CrmContactRow>>({});
  useEffect(() => setPatch({}), [contactId]);
  const patchedRow = useMemo(
    () => (baseRow ? ({ ...baseRow, ...patch } as CrmContactRow) : baseRow),
    [baseRow, patch]
  );
  const applyPatch = useCallback(
    (next: Partial<CrmContactRow>) => setPatch((current) => ({ ...current, ...next })),
    []
  );

  const pooled = usePooledRowActions(patchedRow ?? null, applyPatch);

  const { setStatus, toggleHot } = useCrmMutations({
    patchContact: (_ownerUserId, _contactId, next) => {
      setPatch((current) => ({ ...current, ...next }));
      onContactPatched?.(next);
    },
    onError: () => enqueueSnackbar('שמירת הסטטוס נכשלה', { variant: 'error' })
  });

  // A row the drawer asked for itself has no caller keeping it current, so the panel's live
  // document is projected onto it here (the panel does that for the viewer's own row only).
  const handleContactPatched = ownRow
    ? (next: Partial<CrmContactRow>) => {
        applyPatch(next);
        onContactPatched?.(next);
      }
    : onContactPatched;
  // A shared office's record: editable on the office's rule (held, or the full pool), and read
  // under the office's scope. A caller's own word on either still wins.
  const effectiveCanEdit = canEdit ?? (pooled ? pooled.access.canEdit : undefined);
  const effectiveScopeIsOwnBook = pooled ? false : scopeIsOwnBook;
  const effectiveOrgId = orgId ?? pooled?.orgId ?? null;

  return (
    <Drawer
      anchor={canDock ? "right" : "bottom"}
      open={Boolean(contactId)}
      onClose={onClose}
      // The modal layer itself — see the stacking note above.
      sx={{ zIndex: (t) => t.zIndex.modal }}
      PaperProps={{
        sx: canDock
          ? { width: 520, maxWidth: "100vw", height: "100%", overflow: "hidden" }
          : { height: "92vh", borderTopLeftRadius: 16, borderTopRightRadius: 16, overflow: "hidden" }
      }}
    >
      {contactId && !asked.settled && (
        // The one request before the panel (see the header): its header's shape, briefly.
        <Stack spacing={1.5} sx={{ p: 2.5 }} aria-busy="true">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Skeleton variant="circular" width={44} height={44} />
            <Box sx={{ flexGrow: 1 }}>
              <Skeleton width="55%" height={26} />
              <Skeleton width="35%" />
            </Box>
          </Stack>
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1.5 }} />
        </Stack>
      )}
      {contactId && asked.settled && (
        <CrmContactPanel
          contactId={contactId}
          row={patchedRow}
          scopeIsOwnBook={effectiveScopeIsOwnBook}
          orgId={effectiveOrgId}
          canEdit={effectiveCanEdit}
          pooled={pooled}
          presentation={canDock ? "docked" : "sheet"}
          onClose={onClose}
          onContactPatched={handleContactPatched}
          onContactRekeyed={onContactRekeyed}
          // Only when the row is genuinely editable: `CrmStatusControl` renders read-only without a
          // handler, and `useCrmMutations` patches optimistically BEFORE the write, so a control
          // that slipped through would repaint a colleague's status and then fail silently.
          onSetStatus={
            effectiveCanEdit === false ? undefined : (target, status, sub) => void setStatus(target, status, sub)
          }
          onToggleHot={effectiveCanEdit === false ? undefined : (target) => void toggleHot(target)}
          // A new tab, not navigation: this panel is open over a listing dialog,
          // and routing away would close the listing she is in the middle of.
          onOpenFull={(id) =>
            window.open(
              PATH_DASHBOARD.general.contactDetail(id),
              "_blank",
              "noopener,noreferrer"
            )
          }
        />
      )}
    </Drawer>
  );
}

/**
 * The server's row for ONE contact, for a drawer opened without one: `/contacts/lookup` with
 * that id. Called directly rather than through `useContactLookup`, whose store keeps a
 * document-shaped summary (`ContactSummary`) and not the row: the status strip needs the row's
 * status fields and owner, the "הלקוחות שלי" line its holders. One click, one request.
 *
 * `settled` once an answer, or a failure, is in for THIS id: a phone that is no customer, or a
 * server that cannot be asked, settles with `row: null`, and the panel opens as it always did
 * without a row. An answer for a previous contact is dropped.
 */
function useContactRowOf(contactId: string | null): { row: CrmContactRow | null; settled: boolean } {
  const { contactsLookup } = useWebotServer();
  // `useWebotServer()` rebuilds its functions on every render: the latest one, outside the deps.
  const lookup = useRef(contactsLookup);
  lookup.current = contactsLookup;
  const [answer, setAnswer] = useState<{ contactId: string; row: CrmContactRow | null } | null>(null);

  useEffect(() => {
    if (!contactId) return undefined;
    let cancelled = false;
    lookup
      .current([contactId])
      .then((response) => {
        if (cancelled) return;
        setAnswer({ contactId, row: response.contacts.find((entry) => entry.contactId === contactId) ?? null });
      })
      .catch(() => {
        if (!cancelled) setAnswer({ contactId, row: null });
      });
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  const current = contactId && answer?.contactId === contactId ? answer : null;
  return { row: current?.row ?? null, settled: !contactId || current !== null };
}

/**
 * The "הלקוחות שלי" line for a row of the viewer's OWN shared office (its `org:` key is the
 * viewer's book): /crm's rule for one row (`crmPooledRowAccess`), with add and remove run here
 * and their answer's holder list patched onto the row. `null` for every other row (a personal
 * book's, a colleague's copy, a row not known yet), so the panel draws exactly what it drew.
 *
 * The office's delete is withheld (`canDeleteOfficeRecord: false`): it is office-wide, red and
 * confirmed, and /crm's ⋮ is the one place that offers it.
 */
function usePooledRowActions(
  row: CrmContactRow | null,
  applyPatch: (patch: Partial<CrmContactRow>) => void
): CrmPooledRowActions | null {
  const book = useContactBook();
  const { user } = useAuth();
  const viewerUserId = (user?.email as string | undefined) ?? null;
  const { enqueueSnackbar } = useSnackbar();
  const { addContactHolder, removeContactHolder } = useContactWrites();

  const officeRow = Boolean(
    row &&
      book.isPooled &&
      book.ownBookKey &&
      isOrgBookKey(row.bookKey) &&
      normalizeBookKey(row.bookKey) === normalizeBookKey(book.ownBookKey)
  );
  const access = useCrmMyAccess(book.orgId ?? undefined, { enabled: officeRow });

  const contactId = row?.contactId ?? null;
  const addToMine = useCallback(async () => {
    if (!contactId) return;
    try {
      const response = await addContactHolder({ contactId });
      applyPatch({ holderIds: response.holderIds });
      enqueueSnackbar('הלקוח נוסף ל"הלקוחות שלי"', { variant: "success" });
    } catch (error) {
      enqueueSnackbar(describeContactWriteFailure(error, "ההוספה נכשלה"), { variant: "error" });
    }
  }, [contactId, addContactHolder, applyPatch, enqueueSnackbar]);

  const removeFromMine = useCallback(async () => {
    if (!contactId) return;
    try {
      const response = await removeContactHolder({ contactId });
      applyPatch({ holderIds: response.holderIds });
      enqueueSnackbar('הלקוח הוסר מ"הלקוחות שלי"', { variant: "success" });
    } catch (error) {
      enqueueSnackbar(describeContactWriteFailure(error, "ההסרה נכשלה"), { variant: "error" });
    }
  }, [contactId, removeContactHolder, applyPatch, enqueueSnackbar]);

  const holderIds = row?.holderIds;
  const bookKey = row?.bookKey;
  const ownerUserId = row?.ownerUserId;
  return useMemo(() => {
    if (!officeRow || !bookKey || !ownerUserId) return null;
    const rowAccess = crmPooledRowAccess(
      { ownerUserId, bookKey, holderIds: holderIds ?? [] },
      viewerUserId,
      access.permissions,
      book.isBusy
    );
    return {
      access: { ...rowAccess, canDeleteOfficeRecord: false },
      orgId: book.orgId,
      busy: book.isBusy,
      onAddToMyList: () => void addToMine(),
      onRemoveFromMyList: () => void removeFromMine(),
      onDeleteOfficeRecord: () => undefined
    };
  }, [
    officeRow,
    bookKey,
    ownerUserId,
    holderIds,
    viewerUserId,
    access.permissions,
    book.isBusy,
    book.orgId,
    addToMine,
    removeFromMine
  ]);
}
